import { useMemo } from "react";
import { useAppSelector } from "store";
import { useFleetServiceV1GetFleetsQuery } from "_services/modules/transport-resource/fleet";
import { normalizeDriverName } from "_helper/formatters";
import { dateTimeFormatter } from "_helper/timeFormatter";
import { FleetRow } from "../types";
import { TableSortState } from "components/table";

const mapFleetStatus = (status?: string): FleetRow["status"] => {
  if (
    status === "Available" ||
    status === "Assigned" ||
    status === "Maintenance" ||
    status === "Inactive" ||
    status === "Draft"
  ) {
    return status;
  }
  return "Available";
};

export const useFleetQuery = (sortState: TableSortState) => {
  const searchValue = useAppSelector(state => state.fleet.searchValue);
  const statusFilter = useAppSelector(state => state.fleet.statusFilter);
  const vehicleTypeFilter = useAppSelector(state => state.fleet.vehicleTypeFilter);
  const vehicleModelFilter = useAppSelector(state => state.fleet.vehicleModelFilter);
  const currentPage = useAppSelector(state => state.fleet.currentPage);
  const rowsPerPage = useAppSelector(state => state.fleet.rowsPerPage);

  const sortByMap: Record<string, string> = {
    plateNumber: "plate_number",
    status: "status",
    createdAt: "created_at",
    updatedAt: "updated_at",
    hullNumber: "hull_number",
    vehicleType: "vehicle_type_id",
    vehicleModel: "vehicle_model_id",
    odometer: "odometer",
    stnkExpiry: "stnk_expiry_date",
    kirExpiry: "kir_expiry_date",
    lastMaintenance: "last_maintenance_date",
    driverName: "driver_id",
  };

  const { data: apiResponse, isLoading, isFetching } = useFleetServiceV1GetFleetsQuery({
    page: currentPage,
    limit: rowsPerPage,
    search: searchValue,
    status: statusFilter || "Available,Assigned,Maintenance,Inactive",
    vehicleTypeId: vehicleTypeFilter,
    vehicleModelId: vehicleModelFilter,
    sortBy: sortState ? sortByMap[sortState.columnId] : undefined,
    order: sortState?.direction,
  });

  const tableData: FleetRow[] = useMemo(() => {
    if (!apiResponse?.fleet) return [];
    return apiResponse.fleet
      .map((item: any) => {
        const createdByName = item.created_by || "-";
        const formattedCreatedAt = item.created_at ? dateTimeFormatter(item.created_at) : "-";
        const createdAt = formattedCreatedAt === "—" ? "-" : formattedCreatedAt;

        const rawUpdatedByName = item.updated_by || (createdByName !== "-" ? createdByName : undefined);
        const rawUpdatedAt = item.updated_at || item.created_at;
        const formattedUpdatedAt = rawUpdatedAt ? dateTimeFormatter(rawUpdatedAt) : "-";
        const updatedAt = formattedUpdatedAt === "—" ? "-" : formattedUpdatedAt;
        const updatedBy = rawUpdatedByName && rawUpdatedByName.trim() !== "" ? rawUpdatedByName : "-";

        return {
          id: item.id,
          fleetPicture: item.photo_url || "",
          plateNumber: item.plate_number,
          fleetCode: item.fleet_code || "",
          vehicleType: item.vehicle_type,
          vehicleModel: item.vehicle_model,
          odometer: item.odometer,
          status: mapFleetStatus(item.status),
          stnkExpiry: item.stnk_expiry,
          kirExpiry: item.kir_expiry,
          lastMaintenance: item.last_maintenance_date,
          createdBy: createdByName,
          createdAt: createdAt,
          updatedBy: updatedBy,
          updatedAt: updatedAt,
          driverId: item.driver_id || "",
          driverName: normalizeDriverName(item.driver_name, item.driver_id, "Unassigned"),
        };
      });
  }, [apiResponse]);

  return {
    tableData,
    isLoading,
    isFetching,
    totalPages: apiResponse?.metadata?.total_page || 0,
    currentPage,
    rowsPerPage,
  };
};
